import { isDefined } from "@clipboard-health/util-ts";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import type { CancelShiftBottomSheetProps } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/components/CancelShiftBottomSheet";
import { isAfter } from "date-fns";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";

import { GUIDED_SUPPORT_FULL_PATH } from "../../paths";

interface UseSupportCancelShiftFlowParams {
  shiftId: string;
  shiftStartDate: Date | undefined;
}

interface UseSupportCancelShiftFlowResult {
  startCancelFlow: () => void;
  cancelShiftBottomSheetProps: CancelShiftBottomSheetProps | undefined;
}

/**
 * Simplified cancel flow hook for the Support context.
 *
 * Always uses the CancelShiftBottomSheet (not the Emergency Credits full-page flow)
 * because the EC CancelShiftPage does not support an onShiftCancelled callback,
 * and we need to navigate back to the guided support page after cancellation.
 */
export function useSupportCancelShiftFlow(
  params: UseSupportCancelShiftFlowParams
): UseSupportCancelShiftFlowResult {
  const { shiftId, shiftStartDate } = params;

  const history = useHistory();
  const modalState = useModalState();
  const currentDate = useCurrentDate(1000);

  const isNoCallNoShow = useMemo(() => {
    if (!isDefined(shiftStartDate)) {
      return false;
    }

    return isAfter(currentDate, shiftStartDate);
  }, [currentDate, shiftStartDate]);

  const startCancelFlow = useCallback(() => {
    modalState.openModal();
  }, [modalState]);

  const onShiftCancelled = useCallback(() => {
    // Use replace (not push) so the cancelled shift page is removed from
    // browser history — pressing back won't return to a stale page.
    history.replace(GUIDED_SUPPORT_FULL_PATH);
  }, [history]);

  const cancelShiftBottomSheetProps = useMemo((): CancelShiftBottomSheetProps | undefined => {
    if (!isDefined(shiftStartDate) || !isDefined(shiftId)) {
      return undefined;
    }

    return {
      modalState,
      isNoCallNoShow,
      shiftStartDate,
      shiftId,
      currentDate,
      isOrientationBlockShift: false,
      onShiftCancelled,
    };
  }, [shiftStartDate, shiftId, modalState, isNoCallNoShow, currentDate, onShiftCancelled]);

  return { startCancelFlow, cancelShiftBottomSheetProps };
}
